import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useReactToPrint } from 'react-to-print';
import Api from '../../api/global.service';
import { useParams, useHistory } from 'react-router-dom';
import CardBasic from '../../components/card/card-basic.jsx';
import BlockUi from 'react-block-ui';
import GoogleMapReact from 'google-map-react';
import env from './../../environment.js';
import MapText from '../../components/card/map-text';
import useArrowTextureOverlay from '../../hooks/useArrowTextureOverlay.jsx';
import NorthIcon from '../../components/map/north-icon.jsx';
import parsePoint from '../../utils/parsePoint';
import BackButton from '../../components/link/back-button.jsx';
import CardContainer from '../../components/card/card-container.jsx';
import { useCoopConfig, etiquetaParcelas, generoParcelas, usaMiel } from '../../components/content/coop-config.jsx';
import FormSelectMaterial from '../../nuup/componentes/Forms/FormSelectMaterial.jsx';
import useCroquisHook from '../../hooks/useCroquisHook.jsx';
import useGetColindancias from '../../hooks/useGetColindancias.jsx';
import MarkerInfoWindow from '../../components/map/marker-infowindow.jsx';
import useGeoPuntosLayer from '../../hooks/useGeoPuntosLayer.jsx';
import { pageStyle, prepararMapaParaImprimir, InfoCard, InfoField, PrintSimbologiaTable } from '../../components/print/print-helpers.jsx';

// A partir de este número de puntos del polígono, la tabla de coordenadas se
// imprime en su propia hoja (con salto de página) para que nunca quede cortada
// debajo del mapa. Ajustable según pruebas de impresión.
const UMBRAL_PUNTOS_NUEVA_HOJA = 15;

// Colindancia box — inline-block table cell, print-safe (no absolute positioning)
const ColindantePrintBox = ({ title, data }) => {
  if (!data?.colindante && !data?.propietario) {
    return <div style={{ minHeight: 38 }} />;
  }
  return (
    <div
      className="print-col-box"
      style={{
        border: '1px solid #c8d1a0',
        borderRadius: 5,
        padding: '4px 8px',
        backgroundColor: 'rgba(249,250,244,0.95)',
        fontSize: 10,
        textAlign: 'center',
        minWidth: 90,
        maxWidth: 160,
        display: 'inline-block',
      }}>
      <div
        style={{
          fontWeight: 700,
          color: '#4A6700',
          textTransform: 'uppercase',
          fontSize: 10,
          letterSpacing: 0.3,
        }}>
        {title}
      </div>
      {data?.colindante && <div style={{ fontSize: 10, color: '#333', marginTop: 1 }}>{data.colindante}</div>}
      {data?.colindante_otro && <div style={{ fontSize: 10, color: '#555' }}>{data.colindante_otro}</div>}
      {data?.propietario && <div style={{ fontSize: 9, color: '#666', marginTop: 1 }}>{data.propietario}</div>}
    </div>
  );
};

const ParcelaProfilePrint = () => {
  const componentRef = useRef(null);
  const mapWrapperRef = useRef(null);
  const restaurarMapaRef = useRef(null);
  const { uuid } = useParams();
  const { mostrarAreaCalculada, activeCicloSIC } = useCoopConfig();
  const history = useHistory();
  const [parcela, setParcela] = useState({});
  const [parcelaData, setParcelaData] = useState({});
  const [centroid, setCentroid] = useState(null);
  const [blocking, setBlocking] = useState(false);
  const [geoPuntos, setGeoPuntos] = useState([]);
  const { areaGMaps, renderMap, center, angle, pointsOnMap, lengths, zoom, centroidProcessed, map, maps } = useCroquisHook(
    parcelaData,
    null,
    true
  );
  const { legendData } = useGeoPuntosLayer(map, maps, geoPuntos);
  const [nameToPrint, setNameToPrint] = useState(`Croquis${etiquetaParcelas()}`);
  const [mapType, setMapType] = useState('terrain');
  useArrowTextureOverlay(map, maps, parcelaData?.parcelaPolygon, angle, mapType);
  const { colindancias, pendientes } = useGetColindancias({
    uuid,
    setBlocking,
  });

  const reactToPrintContent = useCallback(() => componentRef.current, []);

  // Deshace el ajuste temporal que prepararMapaParaImprimir le hizo al mapa.
  const restaurarMapa = () => {
    if (restaurarMapaRef.current) restaurarMapaRef.current();
    restaurarMapaRef.current = null;
  };

  const handlePrint = useReactToPrint({
    content: reactToPrintContent,
    documentTitle: nameToPrint,
    onAfterPrint: useCallback(() => history.goBack(), [history]),
    removeAfterPrint: true,
    copyStyles: true,
    pageStyle: pageStyle,
    // Ajusta el mapa al ancho de la hoja y espera sus mosaicos antes de que
    // react-to-print copie el DOM al iframe de impresión.
    onBeforeGetContent: () =>
      prepararMapaParaImprimir(mapWrapperRef.current, map, maps).then(restaurar => {
        restaurarMapaRef.current = restaurar;
      }),
    // Para este punto el DOM ya se copió al iframe: se puede devolver el mapa
    // en pantalla a su ancho normal.
    onBeforePrint: restaurarMapa,
    onPrintError: restaurarMapa,
  });

  useEffect(() => {
    if (uuid != null) {
      setBlocking(true);
      Api.get('parcelas_croquis', uuid)
        .then(response => {
          setParcela(response.data[0]);
          const pol = response.data.find(p => typeof p.poligono === 'string')?.poligono;
          let cent;
          if (response.data[0] && !isNaN(response.data[0]?.centroide_lat) && !isNaN(response?.data[0]?.centroide_lat)) {
            cent = {
              lat: parseFloat(response?.data[0]?.centroide_lat),
              lng: parseFloat(response?.data[0]?.centroide_lng),
            };
            setCentroid(cent);
          }
          const rawAltitudes = response.data[0]?.poligono_altitudes;
          const poligonoAltitudes = rawAltitudes
            ? rawAltitudes
                .split(',')
                .map(s => s.trim())
                .filter(Boolean)
            : null;
          setParcelaData({ parcelaPolygon: pol, centroid: cent, poligonoAltitudes });
          setNameToPrint(`Croquis${etiquetaParcelas()}_${response.data[0].nombre}_${response.data[0].cuenta}`);
          setBlocking(false);
        })
        .catch(e => {
          // eslint-disable-next-line no-console
          console.log('error:', e);
          setBlocking(false);
        });

      Api.get('parcela_geos_puntos', uuid)
        .then(response => setGeoPuntos(response.data || []))
        .catch(() => setGeoPuntos([]));
    }
  }, [uuid]);

  const getLabelClaveProd = () => {
    if (parcela.cuenta_clave_tx && parcela.cuenta_codigo_tx) {
      return ['Clave/Código del productor:', `${parcela.cuenta_clave_tx} / ${parcela.cuenta_codigo_tx}`];
    } else if (parcela.cuenta_clave_tx) {
      return ['Clave del productor:', parcela.cuenta_clave_tx];
    } else if (parcela.cuenta_codigo_tx) {
      return ['Clave del productor:', parcela.cuenta_codigo_tx];
    } else {
      return ['Clave del productor:', parcela.cuenta_id];
    }
  };

  const hasCentroidOnly =
    !parcela.poligono && !!parcelaData?.centroid && !isNaN(parcelaData.centroid.lat) && !isNaN(parcelaData.centroid.lng);
  const hasLegend =
    !!parcela.poligono ||
    hasCentroidOnly ||
    legendData.points.length > 0 ||
    legendData.lines.length > 0 ||
    legendData.polygons.length > 0 ||
    legendData.texts.length > 0;

  const coordsCenter = coords => {
    const lat = coords.reduce((s, c) => s + c.lat, 0) / coords.length;
    const lng = coords.reduce((s, c) => s + c.lng, 0) / coords.length;
    return { lat, lng };
  };

  const panTo = pos => {
    if (map) map.panTo(pos);
  };

  const calcularPendienteEstimada = () => {
    if (pendientes.inspeccion_suelo_pendiente) return pendientes.inspeccion_suelo_pendiente;
    const pts = pointsOnMap.filter(p => p.elevation != null && !isNaN(p.elevation));
    if (pts.length < 2) return null;
    const pMax = pts.reduce((a, b) => (b.elevation > a.elevation ? b : a));
    const pMin = pts.reduce((a, b) => (b.elevation < a.elevation ? b : a));
    const desnivel = pMax.elevation - pMin.elevation;
    const toRad = deg => (deg * Math.PI) / 180;
    const R = 6371000;
    const dLat = toRad(pMin.lat - pMax.lat);
    const dLng = toRad(pMin.lng - pMax.lng);
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(pMax.lat)) * Math.cos(toRad(pMin.lat)) * Math.sin(dLng / 2) ** 2;
    const distHoriz = R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    if (distHoriz < 1) return null;
    return `${((desnivel / distHoriz) * 100).toFixed(1)}% (estimada)`;
  };

  return (
    <div>
      <BlockUi tag="div" message="" blocking={blocking} renderChildren={true}>
        {/* Screen-only controls */}
        <div className="no-print">
          <div className="d-flex align-items-center mt-2 mb-2 justify-content-between">
            <BackButton label="Volver" goBackDefault="/admin/parcelas/list" />
            <button
              onClick={handlePrint}
              className="btn btn-sirio ml-2"
              style={{ borderRadius: '20px' }}
              type="button"
              aria-label={`Imprimir croquis ${generoParcelas('del', 'de la')} ${etiquetaParcelas(['singular', 'lowercase'])}`}>
              <i className="fa fa-print mr-1" aria-hidden="true"></i> Imprimir
            </button>
          </div>

          <div className="row align-items-center mb-3">
            <div className="col-md-8">
              <div
                className="d-flex align-items-start p-3"
                style={{
                  backgroundColor: env.colors.neutral_2,
                  borderRadius: 12,
                  color: env.colors.neutral_700,
                  fontSize: 14,
                }}>
                <i className="fa fa-info-circle mr-2" aria-hidden="true" style={{ color: env.colors.primary_500, marginTop: 3 }}></i>
                <span>
                  <span className="font-weight-bold">Nota:</span> Para mejores resultados al imprimir te sugerimos ajustar el zoom y la
                  posición del mapa.
                </span>
              </div>
            </div>
            <div className="col-md-4">
              <FormSelectMaterial
                id="tipoMapa"
                error={false}
                label="Tipo Mapa"
                value={mapType}
                onChange={v => setMapType(v)}
                height={36}
                options={[
                  { value: 'satellite', label: 'Satelital' },
                  { value: 'terrain', label: 'Terreno' },
                ]}
                showAll={false}
              />
            </div>
          </div>
        </div>

        {/* Printable content */}
        <CardContainer style={{ padding: '24px 28px' }}>
          <div ref={componentRef} className="print-root">
            <div className="print-page print-page-first">
              {/* Header logos */}
              <div className="row justify-content-end mr-3">
                {parcela.logo && (
                  <img
                    style={{ width: 'auto', height: '60px', marginRight: '10px' }}
                    src={`${env.assetURL}${parcela.logo}`}
                    alt="logo_cooperativa"
                  />
                )}
                {parcela.certifications?.map(cert => (
                  <img
                    key={cert.id}
                    style={{ width: 'auto', height: '60px', marginRight: '10px' }}
                    src={`${env.assetURL}${cert.url}`}
                    alt={cert.name || 'logo_certificacion'}
                  />
                ))}
              </div>

              <h2
                className="page-header center"
                style={{
                  textAlign: 'center',
                  fontSize: 18,
                  color: env.colors.primary_600,
                  letterSpacing: '0.04em',
                  borderBottom: `2px solid ${env.colors.primary_300}`,
                  paddingBottom: 8,
                  marginBottom: 16,
                }}>
                CROQUIS POR {etiquetaParcelas(['singular', 'uppercase'])}
              </h2>

              {/* Datos de la parcela agrupados en cards */}
              <div className="row print-info-cards">
                <InfoCard title="Identificación">
                  <InfoField label="Nombre del productor" value={parcela.cuenta} />
                  <InfoField label={getLabelClaveProd()[0].replace(/:$/, '')} value={getLabelClaveProd()[1]} />
                  <InfoField
                    label={`Nombre ${generoParcelas('del', 'de la')} ${etiquetaParcelas(['singular', 'lowercase'])}`}
                    value={parcela.nombre}
                  />
                  <InfoField
                    label={`Código de ${etiquetaParcelas(['singular', 'lowercase'])}`}
                    value={parcela.parcela_codigo ? parcela.parcela_codigo : parcela.id}
                  />
                  {activeCicloSIC() && <InfoField label="Ciclo actual" value={activeCicloSIC()?.nombre} />}
                </InfoCard>
                <InfoCard title="Ubicación">
                  <InfoField label="Municipio" value={parcela.municipio} />
                  <InfoField label="Localidad" value={parcela.localidad} />
                  <InfoField label="Latitud" mono value={parsePoint(parcela?.centroide_lat)} />
                  <InfoField label="Longitud" mono value={parsePoint(parcela?.centroide_lng)} />
                  <InfoField label="Altitud" value={centroidProcessed?.elevation ? `${centroidProcessed.elevation} m` : ''} mono />
                </InfoCard>
                <InfoCard title="Superficie y terreno">
                  {mostrarAreaCalculada() && <InfoField label="Área (ha calculadas)" value={areaGMaps} mono />}
                  <InfoField label="Área (ha reportadas por el productor)" value={parcela.superficie} mono />
                  <InfoField label="Pendiente" value={calcularPendienteEstimada()} />
                  {usaMiel() && <InfoField label="Colmenas" value={parcela.plantas} mono />}
                </InfoCard>
              </div>

              {/* Full-width map with colindancia overlays */}
              <div
                className="map-rounded map-hide-report-error print-map-wrapper"
                ref={mapWrapperRef}
                style={{
                  position: 'relative',
                  height: 540,
                  width: '100%',
                  marginBottom: 4,
                }}>
                {/* Colindancia overlay boxes */}
                <div
                  style={{
                    position: 'absolute',
                    top: 8,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 10,
                    pointerEvents: 'none',
                  }}>
                  <ColindantePrintBox title="Norte" data={colindancias.norte} />
                </div>
                <div
                  style={{
                    position: 'absolute',
                    bottom: 8,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 10,
                    pointerEvents: 'none',
                  }}>
                  <ColindantePrintBox title="Sur" data={colindancias.sur} />
                </div>
                <div
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: 8,
                    transform: 'translateY(-50%)',
                    zIndex: 10,
                    pointerEvents: 'none',
                  }}>
                  <ColindantePrintBox title="Oeste" data={colindancias.oeste} />
                </div>
                <div
                  style={{
                    position: 'absolute',
                    top: '50%',
                    right: 8,
                    transform: 'translateY(-50%)',
                    zIndex: 10,
                    pointerEvents: 'none',
                  }}>
                  <ColindantePrintBox title="Este" data={colindancias.este} />
                </div>

                <NorthIcon color={mapType === 'satellite' ? 'white' : 'black'} />
                {parcela.poligono ? (
                  <>
                    <GoogleMapReact
                      bootstrapURLKeys={{
                        key: env.googleApiKey,
                        libraries: ['geometry', 'elevation'],
                      }}
                      options={() => ({
                        mapTypeId: mapType,
                        rotateControl: true,
                        keyboardShortcuts: false,
                      })}
                      center={center}
                      zoom={zoom}
                      yesIWantToUseGoogleMapApiInternals
                      onGoogleApiLoaded={({ map, maps }) => renderMap(map, maps)}>
                      {lengths.map((it, i) => (
                        <MapText
                          lat={it?.lat}
                          lng={it?.lng}
                          key={i}
                          text={it?.length + 'm'}
                          style={{
                            color: mapType === 'satellite' ? 'white' : 'black',
                            fontSize: 9,
                          }}
                        />
                      ))}
                      {pointsOnMap.map((it, i) => (
                        <MapText
                          lat={it?.lat}
                          lng={it?.lng}
                          key={i}
                          text={it?.title}
                          style={{
                            color: mapType === 'satellite' ? 'white' : 'black',
                            fontSize: 9,
                          }}
                        />
                      ))}
                    </GoogleMapReact>
                  </>
                ) : null}
                {parcela.poligono === null &&
                  parcelaData?.centroid &&
                  !isNaN(parcelaData?.centroid.lat) &&
                  !isNaN(parcelaData?.centroid.lng) && (
                    <GoogleMapReact
                      bootstrapURLKeys={{ key: env.googleApiKey }}
                      options={() => ({ mapTypeId: mapType, keyboardShortcuts: false })}
                      center={centroid}
                      zoom={zoom}
                      yesIWantToUseGoogleMapApiInternals
                      onGoogleApiLoaded={({ map, maps }) => renderMap(map, maps)}>
                      <MarkerInfoWindow
                        lat={parcelaData?.centroid?.lat}
                        lng={parcelaData?.centroid?.lng}
                        productor={'Polígono pendiente de registrar'}
                        color={'#91B833'}
                      />
                    </GoogleMapReact>
                  )}
                {parcelaData?.centroid === null && parcela.poligono === null && (
                  <CardBasic height={319} label="Ubicación no disponible" value={''} style={{ backgroundColor: env.colors.neutral_1 }} />
                )}
              </div>

              {/* SIMBOLOGÍA — page 1, below map */}
              {hasLegend && (
                <PrintSimbologiaTable
                  legendData={legendData}
                  panTo={panTo}
                  coordsCenter={coordsCenter}
                  extraArea={parcela.poligono ? { label: etiquetaParcelas(), onClick: () => panTo(center) } : null}
                  extraPoint={
                    hasCentroidOnly ? { label: etiquetaParcelas(), color: '#91B833', onClick: () => panTo(parcelaData.centroid) } : null
                  }
                />
              )}
            </div>

            {/* Tabla de coordenadas — fluye tras la simbología; si hay muchos
                puntos, se fuerza a una hoja nueva para no quedar cortada */}
            <div
              className={`print-avoid-break${pointsOnMap.length > UMBRAL_PUNTOS_NUEVA_HOJA ? ' print-page-break-before' : ''}`}
              style={{ marginTop: 16 }}>
              <div style={{ width: '100%' }}>
                <div className="row mb-3" style={{ textAlign: 'center' }}>
                  <div style={{ textAlign: 'center', width: '100%' }}>
                    <h4>Coordenadas del croquis</h4>
                  </div>
                </div>
                <table className="table table-bordered" type="text/css" style={{ borderRadius: 10, marginTop: 6 }}>
                  <tbody>
                    <tr className="font-weight-bold" style={{ backgroundColor: env.colors.neutral_2 }}>
                      <td>Punto</td>
                      <td>Latitud</td>
                      <td>Longitud</td>
                      <td>Altitud</td>
                    </tr>
                    {pointsOnMap.length > 0 ? (
                      pointsOnMap.map((p, i) => (
                        <tr key={i}>
                          <td>{p.title}</td>
                          <td>{parsePoint(p.lat, 7, true)}</td>
                          <td>{parsePoint(p.lng, 7, true)}</td>
                          <td>{p.elevation ? `${p.elevation}m` : '-'}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4" className="text-center" style={{ color: env.colors.neutral_500 }}>
                          Sin coordenadas registradas
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </CardContainer>
      </BlockUi>
    </div>
  );
};

export default ParcelaProfilePrint;
